import { Navigate } from 'react-router-dom';
import { Alert, AlertTitle, Box } from '@mui/material';

import { useAuthContext } from '../context/auth-context';
import { getViewAs } from '../lib/view-as';
import { viewAsLabel } from './shared/ViewAsSwitcher';
import { ROLES, WEB_ROLES } from '../constants/roles';

/**
 * Envuelve una ruta y exige un permiso.
 *
 *   <Route path="clubes" element={<RequirePermission perm="clubs.view"><ClubPage /></RequirePermission>} />
 *
 * Sustituye a `PrivateAdminRoute`, que decidía por el claim `role` del JWT: con eso
 * un cambio de rol no se reflejaba hasta que caducaba el token. Aquí se lee el
 * contexto, que se refresca contra `/api/auth/me`.
 *
 * Es solo una barrera de interfaz — evita pintar pantallas que van a fallar. La
 * autorización de verdad la hace el servidor en cada petición.
 */
export const RequirePermission = ({ perm, children }) => {
  const { loggedIn, hasPermission, user } = useAuthContext();

  if (!loggedIn) return <Navigate to="/auth/login" replace />;
  if (!perm || hasPermission(perm)) return children;

  const destino = fallbackRouteFor(user, hasPermission);
  if (destino) return <Navigate to={destino} replace />;

  // Sin ninguna pantalla que ofrecer, el aviso se pinta aquí mismo. Mandar al login a
  // quien tiene sesión válida sería un callejón sin salida: se sale del layout, y con
  // él desaparece el control para salir de "Ver como" — que es justo como se llega a
  // este caso, previsualizando un rol que no ve nada.
  return <SinAcceso perm={perm} />;
};

/**
 * Primera pantalla que este usuario sí puede ver, o `null` si no hay ninguna.
 *
 * `'/'` renderiza la página de usuarios, así que no vale como destino genérico: un
 * Secretario de Zona no tiene `users.view` y rebotaría.
 */
export function fallbackRouteFor(user, hasPermission) {
  if (user?.role !== ROLES.SEC && hasPermission('users.view')) return '/';
  if (hasPermission('reports.view')) return '/reports';
  if (hasPermission('clubs.view')) return '/clubes';

  // `Basic` y `director` no son roles del panel web: el formulario de login ya los
  // rechaza, así que una sesión suya aquí está fuera de sitio y se la manda al login
  // en vez de explicarle qué permiso le falta.
  // Ojo con "Ver como": ahí el rol previsualizado puede ser `Basic`, pero `user.role`
  // sigue siendo el real, así que un administrador previsualizando no cae por aquí y
  // conserva el control para salir de la vista previa.
  if (!WEB_ROLES.includes(user?.role)) return '/auth/login';

  return null;
}

function SinAcceso({ perm }) {
  const viewAs = getViewAs();

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', mt: 4 }}>
      <Alert severity={viewAs ? 'warning' : 'info'}>
        <AlertTitle>
          {viewAs ? 'La vista previa no llega hasta aquí' : 'No tienes acceso a esta pantalla'}
        </AlertTitle>
        {viewAs ? (
          <>
            Estás viendo la aplicación como <strong>{viewAsLabel(viewAs)}</strong>, y
            ese rol no puede ver ninguna sección desde aquí. Sal de la vista previa con
            el aviso de arriba para volver a tu cuenta.
          </>
        ) : (
          <>
            Esta pantalla necesita el permiso <code>{perm}</code>. Pídeselo a un
            administrador si crees que deberías tenerlo.
          </>
        )}
      </Alert>
    </Box>
  );
}

export default RequirePermission;
